import React, { useMemo, useState, useEffect } from "react";
import Container from "react-bootstrap/Container";
import { Row, Col, Badge, Form } from "react-bootstrap";
import ClickBack from "../../components/buttons/ClickBack";
import DynamicTable from "../../components/DynamicTable";
import { fetchSignupRiskChecksData } from "./dataService";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import SearchBar from "../../components/SearchBar";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

registerLocale("fr", fr);

const REASON_LABELS = {
  // Signaux email
  domaine_jetable: "Domaine jetable",
  domaine_a_risque: "Domaine à risque",
  local_part_tres_court: "Adresse très courte",
  local_part_genere: "Adresse générée",
  local_part_mot_test: "Mot-clé de test",
  adresse_role_generique: "Adresse générique",
  domaine_deja_signale: "Domaine déjà signalé",
  // Signaux IP / user-agent
  ip_datacenter: "IP datacenter",
  ip_vpn: "VPN / proxy",
  ip_etrangere: "IP étrangère",
  ip_multi_inscriptions: "Plusieurs inscriptions (même IP)",
  ua_script: "User-agent script",
  ua_absent: "User-agent absent",
  // Blocages
  honeypot: "Honeypot rempli",
  // Le piege contient l'e-mail du compte : remplissage automatique du navigateur
  // ou du gestionnaire de mots de passe, pas un bot. Trace sans point de risque.
  honeypot_autofill: "Honeypot rempli par l'autofill (e-mail du compte)",
  soumission_trop_rapide: "Soumission trop rapide",
};

function ListScreen(props) {
  const [checksData, setChecksData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [nbr, setNbr] = useState(1);
  const [dateDebut, setDateDebut] = useState(null);
  const [dateFin, setDateFin] = useState(null);
  const [minScore, setMinScore] = useState("30");
  const [blockedFilter, setBlockedFilter] = useState("");
  const [ipAddress, setIpAddress] = useState("");

  const fetchData = async () => {
    try {
      setLoading(true);
      const filters = {};
      if (dateDebut) filters.date_from = format(dateDebut, "yyyy-MM-dd");
      if (dateFin) filters.date_to = format(dateFin, "yyyy-MM-dd");
      if (minScore !== "") filters.min_score = minScore;
      if (blockedFilter !== "") filters.blocked = blockedFilter;
      if (ipAddress) filters.ip_address = ipAddress;

      const data = await fetchSignupRiskChecksData(nbr, searchTerm, filters);
      setChecksData(data.data);
    } catch (error) {
      console.error("Error fetching signup risk checks data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    setNbr(1);
  };

  useEffect(() => {
    fetchData();
  }, [nbr, searchTerm, dateDebut, dateFin, minScore, blockedFilter, ipAddress]);

  const columns = useMemo(() => {
    return [
      { Header: "ID", accessor: "id" },
      { Header: "Email", accessor: "email" },
      {
        Header: "Statut",
        accessor: "blocked",
        Cell: ({ value }) =>
          value ? <Badge bg="dark">🤖 Bloqué</Badge> : <Badge bg="light" text="dark">Créé</Badge>,
      },
      {
        Header: "Utilisateur",
        accessor: "user",
        Cell: ({ row }) => {
          const user = row.original.user;
          return user ? `${user.prenom || ""} ${user.nom || ""}` : "—";
        },
      },
      {
        Header: "Score",
        accessor: "score",
        Cell: ({ value }) => {
          const variant = value > 70 ? "danger" : value >= 30 ? "warning" : "success";
          return <Badge bg={variant}>{value}/100</Badge>;
        },
      },
      {
        Header: "Raisons",
        accessor: "reasons",
        Cell: ({ value }) => (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
            {(value || []).map((reason) => (
              <Badge bg="secondary" key={reason}>
                {REASON_LABELS[reason] || reason}
              </Badge>
            ))}
          </div>
        ),
      },
      { Header: "IP", accessor: "ip_address", Cell: ({ value }) => value || "—" },
      {
        // Permet de trancher entre un vrai bot et un faux positif : une URL ou
        // l'email du client = autofill d'un gestionnaire de mots de passe ;
        // un lien spam ou du texte aléatoire = bot.
        Header: "Champ piège",
        accessor: "honeypot_value",
        Cell: ({ value }) =>
          value ? (
            <div
              style={{ maxWidth: "180px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
              title={value}
            >
              <code>{value}</code>
            </div>
          ) : (
            "—"
          ),
      },
      {
        Header: "User Agent",
        accessor: "user_agent",
        Cell: ({ value }) => (
          <div
            style={{
              maxWidth: "200px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={value || "—"}
          >
            {value || "—"}
          </div>
        ),
      },
      {
        Header: "Date",
        accessor: "created_at",
        Cell: ({ value }) => format(new Date(value), "dd-MM-yyyy HH:mm:ss", { locale: fr }),
      },
    ].filter(Boolean);
  }, [nbr]);

  if (!checksData) {
    return (
      <Container>
        <p>Chargement...</p>
      </Container>
    );
  }

  return (
    <Container>
      <div className="signupRiskChecksList">
        <p className="h6">Accueil / Administration / Inscriptions à vérifier</p>

        <Row className="pt-4 pb-2">
          <Col style={{ display: "flex", alignItems: "center" }}>
            <ClickBack />
            <h1 className="p-1">Inscriptions à vérifier</h1>
          </Col>
        </Row>

        <SearchBar
          searchTerm={searchTerm}
          handleSearch={handleSearch}
          placeholder="Rechercher par email..."
        />

        <Row className="mb-3 align-items-end">
          <Col xs="auto">
            <Form.Group>
              <Form.Label>Date de début</Form.Label>
              <DatePicker
                selected={dateDebut}
                onChange={(date) => {
                  setDateDebut(date);
                  setNbr(1);
                }}
                dateFormat="dd/MM/yyyy"
                placeholderText="Date de début"
                isClearable
                className="form-control"
                locale="fr"
              />
            </Form.Group>
          </Col>
          <Col xs="auto">
            <Form.Group>
              <Form.Label>Date de fin</Form.Label>
              <DatePicker
                selected={dateFin}
                onChange={(date) => {
                  setDateFin(date);
                  setNbr(1);
                }}
                dateFormat="dd/MM/yyyy"
                placeholderText="Date de fin"
                isClearable
                className="form-control"
                locale="fr"
              />
            </Form.Group>
          </Col>
          <Col xs="auto">
            <Form.Group>
              <Form.Label>Score minimum</Form.Label>
              <Form.Select
                value={minScore}
                onChange={(e) => {
                  setMinScore(e.target.value);
                  setNbr(1);
                }}
              >
                <option value="">Toutes les inscriptions</option>
                <option value="30">🟠 30 et plus</option>
                <option value="70">🔴 70 et plus</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col xs="auto">
            <Form.Group>
              <Form.Label>Type</Form.Label>
              <Form.Select
                value={blockedFilter}
                onChange={(e) => {
                  setBlockedFilter(e.target.value);
                  setNbr(1);
                }}
              >
                <option value="">Tout</option>
                <option value="1">🤖 Bloquées (bots)</option>
                <option value="0">Créées</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col xs="auto">
            <Form.Group>
              <Form.Label>Adresse IP</Form.Label>
              <Form.Control
                type="text"
                placeholder="Filtrer par IP..."
                value={ipAddress}
                onChange={(e) => {
                  setIpAddress(e.target.value);
                  setNbr(1);
                }}
              />
            </Form.Group>
          </Col>
        </Row>

        {loading ? (
          <p className="lead">Chargement...</p>
        ) : (
          <DynamicTable
            columns={columns}
            data={checksData?.data}
            link={checksData?.links}
            last_page={checksData?.last_page}
            setNbr={setNbr}
            nbr={nbr}
            total={checksData?.total}
            name="signupRiskCheck"
          />
        )}
      </div>
    </Container>
  );
}

export default ListScreen;
